import { useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../styles/navbar.css";
import OfflineBanner from "../pages/OfflineBanner";
import ActualizacionDisponible from "./ActualizacionDisponible";
import { useOnlineStatus } from "../hooks/useSync";
import { useAuth } from "../context/AuthContext";
import { APP_VERSION } from "../../../version";

import bannerImage from "../image/Banner.png";

// Iniciales para el avatar: "María José Pérez" -> "MP"
function iniciales(nombre = "") {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

// Menú desplegable con el usuario logueado. Vive en la barra superior para
// que desde CUALQUIER pantalla (una estrategia, el dashboard, admin) se
// pueda volver al inicio o cerrar sesión — antes solo se podía desde el Home.
function MenuUsuario({ user, onLogout }) {
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!abierto) return;
    const cerrar = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    const esc = (e) => { if (e.key === "Escape") setAbierto(false); };
    document.addEventListener("mousedown", cerrar);
    document.addEventListener("touchstart", cerrar);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", cerrar);
      document.removeEventListener("touchstart", cerrar);
      document.removeEventListener("keydown", esc);
    };
  }, [abierto]);

  function ir(path) {
    setAbierto(false);
    navigate(path);
  }

  const esAdmin = user.rol === "admin";

  return (
    <div className="usuario-menu" ref={ref}>
      <button
        type="button"
        className="usuario-boton"
        aria-haspopup="menu"
        aria-expanded={abierto}
        onClick={() => setAbierto((a) => !a)}
        title={user.nombre}
      >
        <span className="usuario-avatar">{iniciales(user.nombre)}</span>
        <span className="usuario-nombre">{user.nombre?.split(" ")[0]}</span>
        <span aria-hidden="true" style={{ fontSize: "0.65rem", opacity: 0.8 }} className="d-none d-sm-inline">▼</span>
      </button>

      {abierto && (
        <div className="usuario-dropdown" role="menu">
          <div className="usuario-dropdown-cabecera">
            <strong>{user.nombre}</strong>
            <small>{esAdmin ? "Administrador" : "Usuario"} · {user.identificacion || ""}</small>
          </div>
          <button type="button" role="menuitem" onClick={() => ir("/")}>🏠 Inicio</button>
          {esAdmin && (
            <>
              <button type="button" role="menuitem" onClick={() => ir("/admin")}>⚙️ Administración de usuarios</button>
              <button type="button" role="menuitem" onClick={() => ir("/dashboard")}>📊 Dashboard</button>
            </>
          )}
          <button
            type="button"
            role="menuitem"
            className="peligro"
            onClick={() => { setAbierto(false); onLogout(); navigate("/login"); }}
          >
            ⎋ Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}

export default function MainLayout({ children }) {
  const online = useOnlineStatus();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <>
      <nav className="navbar-main d-flex align-items-center w-100">
        {/* ── Logo ── */}
        <div
          className="navbar-logos"
          onClick={() => navigate("/")}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter") navigate("/"); }}
          aria-label="Ir al inicio"
        >
          <img src={bannerImage} alt="Secretaría de Salud Departamental" className="navbar-logo" />
        </div>

        {/* ── Título centrado (solo escritorio) ── */}
        <div className="titulo-navbar">
          Sistema Integrado de Promoción Social
          <span>Secretaría de Salud del Amazonas</span>
        </div>

        {/* ── Derecha: conexión + usuario ── */}
        <div className="navbar-right">
          <div className={`conexion-badge ${online ? "online" : "offline"}`} title={online ? "En línea" : "Sin conexión"}>
            <span className="conexion-dot" />
            <span className="conexion-texto">{online ? "En línea" : "Sin conexión"}</span>
          </div>
          {user && <MenuUsuario user={user} onLogout={logout} />}
        </div>
      </nav>

      {/* ── Contenido principal ── */}
      <main className="main-content">
        <Container fluid="lg">{children}</Container>
      </main>

      {/* ── Footer ── */}
      <footer className="footer-main">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-title">Sistema Integrado de Promoción Social</div>
            <div className="footer-sub">Secretaría de Salud del Amazonas · 2026 · v{APP_VERSION}</div>
          </div>

          <div className="footer-section footer-center">
            <div className="footer-dev">
              💻 Desarrollado por <strong>Promoción Social</strong>
            </div>
            <div className="footer-links">
              <a href="tel:+573235763653">📞 +57 323 576 3653</a>
            </div>
          </div>

          <div className="footer-section footer-right">
            <div className="footer-copy">© 2026 Promoción Social</div>
            <div className="footer-copy">Todos los derechos reservados</div>
          </div>
        </div>
      </footer>

      <OfflineBanner />
      <ActualizacionDisponible />
    </>
  );
}
